import React, { useState } from "react";
import {
	Box,
	TextField,
	Typography,
	Button,
	IconButton,
	Avatar,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LanguageIcon from "@mui/icons-material/Language";
import BusinessIcon from "@mui/icons-material/Business";

function AbaPlataformaRastreio({ dados, setDados }) {
	const [foto, setFoto] = useState(dados.foto || null);
	const [fotoPreview, setFotoPreview] = useState(null);
	const [galeria, setGaleria] = useState(dados.galeria || []);

	const handleChange = (field, value) => {
		setDados((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const formatWhatsApp = (digits) => {
		if (!digits) return "";
		const d = digits.replace(/\D/g, "");
		if (d.length <= 10) {
			return d
				.replace(/(\d{2})(\d)/, "($1) $2")
				.replace(/(\d{4})(\d)/, "$1-$2");
		}
		return d
			.replace(/(\d{2})(\d)/, "($1) $2")
			.replace(/(\d{5})(\d)/, "$1-$2");
	};

	const handleFotoUpload = (event) => {
		const file = event.target.files[0];
		if (file) {
			// Validar tamanho (máx 5MB)
			if (file.size > 5 * 1024 * 1024) {
				alert("A imagem deve ter no máximo 5MB");
				return;
			}

			// Validar tipo
			const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
			if (!allowedTypes.includes(file.type)) {
				alert("Apenas arquivos JPG e PNG são permitidos");
				return;
			}

			setFoto(file);
			handleChange("foto", file);

			// Criar preview
			const reader = new FileReader();
			reader.onloadend = () => {
				setFotoPreview(reader.result);
			};
			reader.readAsDataURL(file);
		}
	};

	const handleRemoverFoto = () => {
		setFoto(null);
		setFotoPreview(null);
		handleChange("foto", null);
	};

	const handleGaleriaUpload = (event) => {
		const files = Array.from(event.target.files);
		const galeriaAtual = galeria.length;
		const espacoDisponivel = 10 - galeriaAtual;

		if (files.length > espacoDisponivel) {
			alert(`Você pode adicionar apenas ${espacoDisponivel} imagem(ns). Limite máximo: 10 imagens.`);
			return;
		}

		// Validar cada arquivo
		const arquivosValidos = [];
		for (const file of files) {
			// Validar tamanho (máx 5MB)
			if (file.size > 5 * 1024 * 1024) {
				alert(`A imagem ${file.name} excede o tamanho máximo de 5MB`);
				continue;
			}

			// Validar tipo
			const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
			if (!allowedTypes.includes(file.type)) {
				alert(`Arquivo ${file.name} não é uma imagem válida (JPG ou PNG)`);
				continue;
			}

			arquivosValidos.push(file);
		}

		if (arquivosValidos.length > 0) {
			const novaGaleria = [...galeria, ...arquivosValidos];
			setGaleria(novaGaleria);
			handleChange("galeria", novaGaleria);
		}
	};

	const handleRemoverImagemGaleria = (index) => {
		const novaGaleria = galeria.filter((_, i) => i !== index);
		setGaleria(novaGaleria);
		handleChange("galeria", novaGaleria);
	};

	return (
		<Box>
			{/* BLOCO 1: DESCRIÇÃO DA EMPRESA */}
			<Box
				sx={{
					bgcolor: "white",
					borderRadius: "8px",
					p: 3,
					mb: 3,
					boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
				}}
			>
				<Typography
					variant="subtitle2"
					sx={{
						fontWeight: 600,
						fontSize: "13px",
						color: "#666",
						mb: 3,
						textTransform: "uppercase",
						letterSpacing: "0.5px",
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					<BusinessIcon sx={{ fontSize: 18 }} />
					Descrição da Empresa
				</Typography>

				<Box sx={{ display: "flex", gap: 3, mb: 3 }}>
					{/* Upload de Foto/Logo */}
					<Box sx={{ flexShrink: 0 }}>
						<Typography
							variant="caption"
							sx={{
								color: "#666",
								fontSize: "12px",
								fontWeight: 600,
								display: "block",
								mb: 1.5,
							}}
						>
							Logo ou Foto
						</Typography>
						<Box
							sx={{
								position: "relative",
								width: 120,
								height: 120,
							}}
						>
							{foto || fotoPreview ? (
								<>
									<Avatar
										src={fotoPreview || (typeof foto === "string" ? foto : "")}
										sx={{
											width: 120,
											height: 120,
											borderRadius: "12px",
											border: "2px solid #e8e8e8",
										}}
										variant="rounded"
									>
										<BusinessIcon sx={{ fontSize: 48, color: "#ccc" }} />
									</Avatar>
									<IconButton
										onClick={handleRemoverFoto}
										sx={{
											position: "absolute",
											top: -8,
											right: -8,
											bgcolor: "#ff4d4d",
											color: "white",
											width: 28,
											height: 28,
											"&:hover": {
												bgcolor: "#c82333",
											},
										}}
										size="small"
									>
										<DeleteIcon sx={{ fontSize: 16 }} />
									</IconButton>
								</>
							) : (
								<Button
									component="label"
									sx={{
										width: 120,
										height: 120,
										border: "2px dashed #e0e0e0",
										borderRadius: "12px",
										bgcolor: "#fafafa",
										display: "flex",
										flexDirection: "column",
										gap: 1,
										textTransform: "none",
										color: "#999",
										"&:hover": {
											bgcolor: "#f5f5f5",
											borderColor: "#1a9e8f",
											color: "#1a9e8f",
										},
									}}
								>
									<AddPhotoAlternateIcon sx={{ fontSize: 32 }} />
									<Typography sx={{ fontSize: "11px" }}>
										Adicionar
									</Typography>
									<input
										type="file"
										hidden
										accept="image/jpeg,image/jpg,image/png"
										onChange={handleFotoUpload}
									/>
								</Button>
							)}
						</Box>
						<Typography
							variant="caption"
							sx={{
								color: "#999",
								fontSize: "11px",
								display: "block",
								mt: 1,
								textAlign: "center",
							}}
						>
							JPG ou PNG (máx 5MB)
						</Typography>
					</Box>

					{/* Campos de Texto */}
					<Box sx={{ flex: 1 }}>
						<TextField
							fullWidth
							label="Nome da Distribuidora"
							value={dados.nomeDistribuidora || ""}
							onChange={(e) => handleChange("nomeDistribuidora", e.target.value)}
							placeholder="Ex: Distribuidora Agro Sul de Minas"
							InputLabelProps={{ style: { fontSize: "14px" } }}
							inputProps={{ style: { fontSize: "15px" } }}
							sx={{
								mb: 2,
								"& .MuiOutlinedInput-root": {
									bgcolor: "white",
								},
							}}
						/>

						<TextField
							fullWidth
							label="Descrição da Empresa"
							value={dados.descricaoEmpresa || ""}
							onChange={(e) => handleChange("descricaoEmpresa", e.target.value)}
							multiline
							rows={4}
							placeholder="Conte a história da sua empresa, valores, diferenciais e missão..."
							InputLabelProps={{ style: { fontSize: "14px" } }}
							inputProps={{ style: { fontSize: "15px" } }}
							sx={{
								"& .MuiOutlinedInput-root": {
									bgcolor: "white",
								},
							}}
						/>
					</Box>
				</Box>
			</Box>

			{/* BLOCO 2: REDES SOCIAIS */}
			<Box
				sx={{
					bgcolor: "white",
					borderRadius: "8px",
					p: 3,
					mb: 3,
					boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
				}}
			>
				<Typography
					variant="subtitle2"
					sx={{
						fontWeight: 600,
						fontSize: "13px",
						color: "#666",
						mb: 3,
						textTransform: "uppercase",
						letterSpacing: "0.5px",
					}}
				>
					Redes Sociais e Contato
				</Typography>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(2, 1fr)",
						gap: 2.5,
					}}
				>
					{/* WhatsApp */}
					<TextField
						fullWidth
						label="WhatsApp"
						value={formatWhatsApp(dados.whatsapp)}
						onChange={(e) => handleChange("whatsapp", e.target.value.replace(/\D/g, ""))}
						placeholder="(00) 00000-0000"
						InputLabelProps={{ style: { fontSize: "14px" } }}
						inputProps={{ style: { fontSize: "15px" } }}
						InputProps={{
							startAdornment: (
								<WhatsAppIcon
									sx={{
										color: "#25D366",
										fontSize: 20,
										mr: 1,
									}}
								/>
							),
						}}
						sx={{
							"& .MuiOutlinedInput-root": {
								bgcolor: "white",
							},
						}}
					/>

					{/* Instagram */}
					<TextField
						fullWidth
						label="Instagram"
						value={dados.instagram || ""}
						onChange={(e) => handleChange("instagram", e.target.value)}
						placeholder="@seuusuario ou https://instagram.com/..."
						InputLabelProps={{ style: { fontSize: "14px" } }}
						inputProps={{ style: { fontSize: "15px" } }}
						InputProps={{
							startAdornment: (
								<InstagramIcon
									sx={{
										color: "#E4405F",
										fontSize: 20,
										mr: 1,
									}}
								/>
							),
						}}
						sx={{
							"& .MuiOutlinedInput-root": {
								bgcolor: "white",
							},
						}}
					/>

					{/* Facebook */}
					<TextField
						fullWidth
						label="Facebook"
						value={dados.facebook || ""}
						onChange={(e) => handleChange("facebook", e.target.value)}
						placeholder="https://facebook.com/..."
						InputLabelProps={{ style: { fontSize: "14px" } }}
						inputProps={{ style: { fontSize: "15px" } }}
						InputProps={{
							startAdornment: (
								<FacebookIcon
									sx={{
										color: "#1877F2",
										fontSize: 20,
										mr: 1,
									}}
								/>
							),
						}}
						sx={{
							"& .MuiOutlinedInput-root": {
								bgcolor: "white",
							},
						}}
					/>

					{/* Website */}
					<TextField
						fullWidth
						label="Website"
						value={dados.website || ""}
						onChange={(e) => handleChange("website", e.target.value)}
						placeholder="https://www.seusite.com.br"
						InputLabelProps={{ style: { fontSize: "14px" } }}
						inputProps={{ style: { fontSize: "15px" } }}
						InputProps={{
							startAdornment: (
								<LanguageIcon
									sx={{
										color: "#1a9e8f",
										fontSize: 20,
										mr: 1,
									}}
								/>
							),
						}}
						sx={{
							"& .MuiOutlinedInput-root": {
								bgcolor: "white",
							},
						}}
					/>
				</Box>
			</Box>

			{/* BLOCO 3: GALERIA DE IMAGENS */}
			<Box
				sx={{
					bgcolor: "white",
					borderRadius: "8px",
					p: 3,
					boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 3,
					}}
				>
					<Box>
						<Typography
							variant="subtitle2"
							sx={{
								fontWeight: 600,
								fontSize: "13px",
								color: "#666",
								textTransform: "uppercase",
								letterSpacing: "0.5px",
							}}
						>
							Galeria de Imagens
						</Typography>
						<Typography
							variant="caption"
							sx={{
								color: "#999",
								fontSize: "13px",
								mt: 0.5,
								display: "block",
							}}
						>
							{galeria.length > 0
								? `${galeria.length} de 10 ${galeria.length === 1 ? "imagem adicionada" : "imagens adicionadas"}`
								: "Nenhuma imagem adicionada"}
						</Typography>
					</Box>
					{galeria.length < 10 && (
						<Button
							variant="contained"
							component="label"
							startIcon={<AddPhotoAlternateIcon />}
							sx={{
								textTransform: "none",
								bgcolor: "#1a9e8f",
								fontSize: "14px",
								px: 2.5,
								py: 1,
								boxShadow: "none",
								"&:hover": {
									bgcolor: "#138a7d",
									boxShadow: "0 2px 4px rgba(23, 162, 184, 0.3)",
								},
							}}
						>
							Adicionar Imagens
							<input
								type="file"
								hidden
								accept="image/jpeg,image/jpg,image/png"
								multiple
								onChange={handleGaleriaUpload}
							/>
						</Button>
					)}
				</Box>

				{galeria.length === 0 ? (
					<Box
						sx={{
							textAlign: "center",
							py: 5,
							px: 3,
							border: "2px dashed #e0e0e0",
							borderRadius: "8px",
							bgcolor: "#f9f9f9",
						}}
					>
						<Box
							sx={{
								width: 56,
								height: 56,
								borderRadius: "50%",
								bgcolor: "#E7F6F8",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								margin: "0 auto",
								mb: 2,
							}}
						>
							<AddPhotoAlternateIcon sx={{ color: "#1a9e8f", fontSize: 28 }} />
						</Box>
						<Typography
							variant="body2"
							sx={{
								fontSize: "15px",
								color: "#666",
								fontWeight: 500,
								mb: 0.5,
							}}
						>
							Nenhuma imagem na galeria
						</Typography>
						<Typography
							variant="caption"
							sx={{
								fontSize: "13px",
								color: "#999",
							}}
						>
							Adicione até 10 imagens para compor a galeria da sua empresa
						</Typography>
					</Box>
				) : (
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
							gap: 2,
						}}
					>
						{galeria.map((img, index) => {
							// Criar preview da imagem
							let imgSrc = "";
							if (img instanceof File) {
								imgSrc = URL.createObjectURL(img);
							} else if (typeof img === "string") {
								imgSrc = img;
							}

							return (
								<Box
									key={index}
									sx={{
										position: "relative",
										paddingTop: "100%", // Aspect ratio 1:1
										borderRadius: "8px",
										overflow: "hidden",
										border: "1px solid #e8e8e8",
										transition: "all 0.2s ease",
										"&:hover": {
											boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
											"& .delete-overlay": {
												opacity: 1,
											},
										},
									}}
								>
									{/* Imagem */}
									<Box
										component="img"
										src={imgSrc}
										alt={`Galeria ${index + 1}`}
										sx={{
											position: "absolute",
											top: 0,
											left: 0,
											width: "100%",
											height: "100%",
											objectFit: "cover",
										}}
									/>

									{/* Overlay com botão de deletar */}
									<Box
										className="delete-overlay"
										sx={{
											position: "absolute",
											top: 0,
											left: 0,
											right: 0,
											bottom: 0,
											bgcolor: "rgba(0,0,0,0.5)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											opacity: 0,
											transition: "opacity 0.2s ease",
										}}
									>
										<IconButton
											onClick={() => handleRemoverImagemGaleria(index)}
											sx={{
												bgcolor: "white",
												color: "#ff4d4d",
												"&:hover": {
													bgcolor: "#ff4d4d",
													color: "white",
												},
											}}
										>
											<DeleteIcon />
										</IconButton>
									</Box>

									{/* Número da imagem */}
									<Box
										sx={{
											position: "absolute",
											top: 8,
											left: 8,
											bgcolor: "rgba(0,0,0,0.6)",
											color: "white",
											px: 1,
											py: 0.5,
											borderRadius: "4px",
											fontSize: "11px",
											fontWeight: 600,
										}}
									>
										{index + 1}
									</Box>
								</Box>
							);
						})}
					</Box>
				)}

				{galeria.length > 0 && galeria.length < 10 && (
					<Typography
						variant="caption"
						sx={{
							color: "#999",
							fontSize: "12px",
							display: "block",
							mt: 2,
							textAlign: "center",
						}}
					>
						Você pode adicionar mais {10 - galeria.length} {10 - galeria.length === 1 ? "imagem" : "imagens"}
					</Typography>
				)}
			</Box>
		</Box>
	);
}

export default AbaPlataformaRastreio;
